import type { Course, PostEnriched } from '@/lib/db/types';

export interface PersonalCourseBest {
  course: Course;
  best: number;
  post: PostEnriched;
}

export interface PersonalCareerStats {
  allTimeBest: number | null;
  allTimeAvg: string;
  last10Avg: string;
  totalFullRounds: number;
  totalAllRounds: number;
  byCourse: PersonalCourseBest[];
  recentFullRounds: PostEnriched[];
}

function isFullRoundPost(p: PostEnriched): boolean {
  return p.type === 'round' && p.round?.gross_total != null && p.round.holes === 'full';
}

/** My Records stats — full-18 for bests/avgs; 9-hole rounds listed separately in recent. */
export function buildPersonalCareerStats(
  myRoundPosts: PostEnriched[],
  courseList: Course[],
): PersonalCareerStats | null {
  if (myRoundPosts.length === 0) return null;

  const fullRoundPosts = myRoundPosts.filter(isFullRoundPost);
  const fullScores = fullRoundPosts.map((p) => p.round!.gross_total!);

  const allTimeBest = fullScores.length ? Math.min(...fullScores) : null;
  const allTimeAvg = fullScores.length
    ? (fullScores.reduce((a, b) => a + b, 0) / fullScores.length).toFixed(1)
    : '—';
  const last10Full = fullScores.slice(0, 10);
  const last10Avg = last10Full.length
    ? (last10Full.reduce((a, b) => a + b, 0) / last10Full.length).toFixed(1)
    : '—';

  const byCourseMap: Record<string, PersonalCourseBest> = {};
  fullRoundPosts.forEach((p) => {
    const cid = p.round!.course_id;
    const course = courseList.find((c) => c.id === cid);
    if (!course) return;
    const gross = p.round!.gross_total!;
    if (!byCourseMap[cid] || gross < byCourseMap[cid].best) {
      byCourseMap[cid] = { course, best: gross, post: p };
    }
  });

  const byCourse = Object.values(byCourseMap).sort((a, b) => a.best - b.best);

  return {
    allTimeBest,
    allTimeAvg,
    last10Avg,
    totalFullRounds: fullScores.length,
    totalAllRounds: myRoundPosts.length,
    byCourse,
    recentFullRounds: fullRoundPosts.slice(0, 10),
  };
}
